import { Component } from 'react';
import ReactSelect, { createFilter } from 'react-select';
import ReactSelectCreatable from 'react-select/creatable';
import styled from 'styled-components';
import { theme } from '../theme';
import { FormItem, ValidationState } from '../Form/FormItem';
import MenuList from '../Select/SelectMenuList';
import { sortOptions } from '../../utils/sorting';

const RenderSelect = props => {
  return props.children({ ...props });
};

const StyledSelect = styled(ReactSelect)`
  ${props => props.theme.partials.newForm.item.default};
`;

const StyledCreatableSelect = styled(ReactSelectCreatable)`
  ${props => props.theme.partials.newForm.item.default};
`;

const ReactSelectStyles = () => ({
  container: styles => ({
    ...styles,
    padding: 0,
  }),
  control: (styles, { isDisabled }) => ({
    ...styles,
    width: '100%',
    minHeight: '50px',
    border: 'none',
    background: 'none',
    boxShadow: 'none',
    padding: '0 20px',
    color: isDisabled ? theme.vars.newColor.darkGS3 : null,
    lineHeight: 1.5,

    ':hover': {
      boxShadow: 'none',
    },
  }),
  valueContainer: styles => ({
    ...styles,
    padding: '5px 0',

    '&:hover': {
      cursor: 'text',
    },
  }),
  placeholder: styles => ({
    ...styles,
    color: theme.vars.newColor.darkGS3,
  }),
  multiValue: (styles, { isDisabled, isFocused, data: { fixed } }) => ({
    ...styles,
    alignItems: 'center',
    backgroundColor: isDisabled
      ? theme.vars.newColor.lightGS1
      : fixed
      ? theme.vars.newColor.lightGS1
      : theme.vars.newColor.blue1,
    margin: '5px 10px 5px 0',
    minHeight: '2.8rem',
    borderRadius: 0,
    border: '1px solid transparent',
    borderColor: isFocused && !fixed ? theme.vars.newColor.blue3 : 'transparent',

    '&:hover': {
      borderColor: isDisabled || fixed ? 'transparent' : theme.vars.newColor.blue3,
    },
  }),
  multiValueLabel: (styles, { data: { fixed } }) => ({
    ...styles,
    color: theme.vars.newColor.white,
    fontSize: '100%',
    padding: '2px',
    paddingLeft: '10px',
    paddingRight: fixed ? '10px' : '5px',
  }),
  multiValueRemove: (styles, { isFocused, data: { fixed } }) => ({
    ...styles,
    color: theme.vars.newColor.white,
    cursor: 'pointer',
    display: fixed ? 'none' : 'flex',
    padding: '6px 10px',
    backgroundColor: isFocused ? 'transparent' : null,
    borderRadius: 0,

    ':hover': {
      backgroundColor: 'transparent',
      color: 'white',
    },

    svg: {
      width: '1.4rem',
      height: '1.4rem',
    },
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  clearIndicator: styles => ({
    ...styles,

    svg: {
      fill: theme.vars.newColor.darkGS2,
    },
  }),
  dropdownIndicator: styles => ({
    ...styles,
    color: theme.vars.newColor.darkGS2,
    paddingRight: 0,

    '&:hover': {
      cursor: 'pointer',
    },
  }),
  menu: styles => ({
    ...styles,
    zIndex: 2,
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? theme.vars.newColor.blue1 : isFocused ? theme.vars.newColor.blue4 : null,
  }),
});

export interface ISelectMultiOption<T> {
  value: T;
  label: string;
  fixed?: boolean;
}

export type MenuPlacement = 'auto' | 'bottom' | 'below' | 'top' | 'above';

export interface IProps<T> {
  /** Select label */
  label?: string;
  /** ARIA label tag */
  ariaLabel: string;
  /** Will be passed through to the labels for attribute */
  inputId?: string;
  /**
   * The placeholder to display when no value is specified. Defaults to "Type
   * here...".
   */
  placeholder?: string;
  /** The selected item. Undefined by default. */
  value?: Array<ISelectMultiOption<T>>;
  /** Item to be selected on first load. Undefined by default. */
  defaultValue?: Array<ISelectMultiOption<T>>;
  /** The list of items to select from. {value:string, label:string}. */
  options?: Array<ISelectMultiOption<T>>;
  /** Event invoked when a new value is selected. */
  onChange?: (value: Array<ISelectMultiOption<T>>, action: any) => void;
  /** Event  invoked when a new item is created. */
  onCreateOption?: (inputValue: string) => void;
  /** Input validation state (complete, warning).  */
  validationState?: ValidationState;
  /** Input validation message.  */
  validationMessage?: string;
  /** Adds spacing below the component (default: true). */
  spacingBelow?: boolean;
  /** A hint can be passed as HTML to explain what the select is used for. */
  hint?: any;
  /** Optional tooltip heading.  Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  tooltipDescription?: string;
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  /**
   * The placement of the menu. One of "auto", "bottom", or "top" (defaults to
   * "bottom").
   */
  menuPlacement?: MenuPlacement;
  /** Height of the menu in pixels. Defaults to 300. */
  menuHeight?: number;
  /** Whether to disable the select or not.  Defaults to false. */
  disabled?: boolean;
  /** Whether you can search the options. Defaults to true. */
  searchable?: boolean;
  /** Minimum search term length before options are displayed. Defaults to 0. */
  minLength?: number;
  /** Placeholder which appears if more characters must be typed to search results */
  minLengthPlaceholder?: string;
  /** Placeholder which appears when there are no options to display */
  noOptionsPlaceholder?: string;
  /** Whether you can clear the selected value. Defaults to true. */
  clearable?: boolean;
  /** Whether or not the menu is open on load. Defaults to false. */
  defaultMenuOpen?: boolean;
  /** Whether or not to focus the menu on mount. Defaults to false. */
  autoFocus?: boolean;
  /**
   * react-select doesn't handle being inline very well. So we need to ensure
   * that when it's inline it sets FormItem to full width.
   */
  inline?: boolean;
  /** Whether or not new options can be created. */
  creatable?: boolean;
  /** Alphabetically sort the options by the label name.  */
  isSorted?: boolean;
  /** Callback function that is fired once the select is focused. */
  onFocus?: () => void;
  /** Callback function that is fired once the select has lost focus. */
  onBlur?: () => void;
}

export class SelectMulti<T = string> extends Component<IProps<T>> {
  public static defaultProps: Pick<
    IProps<string>,
    'clearable' | 'minLength' | 'minLengthPlaceholder' | 'noOptionsPlaceholder' | 'searchable'
  > = {
    clearable: true,
    minLength: 0,
    minLengthPlaceholder: 'Type to search...',
    noOptionsPlaceholder: 'No options',
    searchable: true,
  };

  public render() {
    const {
      ariaLabel,
      autoFocus,
      clearable,
      creatable,
      defaultMenuOpen,
      defaultValue,
      disabled,
      hint,
      inline,
      inputId,
      label,
      menuHeight,
      menuPlacement,
      minLength,
      minLengthPlaceholder,
      noOptionsPlaceholder,
      onChange,
      onCreateOption,
      options,
      placeholder,
      searchable,
      spacingBelow,
      tooltipDescription,
      tooltipHeading,
      tooltipWidth,
      validationMessage,
      validationState,
      value,
      onFocus,
      onBlur,
    } = this.props;

    let selectMenuPlacement: MenuPlacement = menuPlacement;

    if (menuPlacement === 'above') {
      selectMenuPlacement = 'top';
    } else if (menuPlacement === 'below') {
      selectMenuPlacement = 'bottom';
    }

    const components = options && options.length >= 100 ? { MenuList } : null;

    return (
      <FormItem
        disabled={disabled}
        fullWidth={inline}
        hint={hint}
        label={label}
        spacingBelow={spacingBelow}
        tooltipDescription={tooltipDescription}
        tooltipHeading={tooltipHeading}
        tooltipWidth={tooltipWidth}
        validationMessage={validationMessage}
        validationState={validationState}
        for={inputId}
      >
        <RenderSelect
          aria-label={ariaLabel}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          className="select-container"
          classNamePrefix="select"
          components={components}
          defaultMenuIsOpen={defaultMenuOpen}
          defaultValue={defaultValue}
          filterOption={this.createOptionFilter(minLength, searchable)}
          inputId={inputId}
          isClearable={clearable}
          isDisabled={disabled}
          isSearchable={searchable}
          isMulti={true}
          maxMenuHeight={menuHeight}
          menuPlacement={selectMenuPlacement}
          noOptionsMessage={this.createNoOptionsMessage(
            minLength,
            searchable,
            minLengthPlaceholder,
            noOptionsPlaceholder,
          )}
          onChange={(selectedOption: Array<ISelectMultiOption<T>>, action: any) => onChange(selectedOption, action)}
          onFocus={onFocus}
          onBlur={onBlur}
          onCreateOption={onCreateOption}
          options={this.orderOptions(options)}
          placeholder={placeholder || 'Type here...'}
          styles={ReactSelectStyles()}
          value={value}
        >
          {selectProps => (
            <>
              {creatable && <StyledCreatableSelect {...selectProps} />}
              {!creatable && <StyledSelect {...selectProps} />}
            </>
          )}
        </RenderSelect>
      </FormItem>
    );
  }

  private createOptionFilter = (minLength: number, isSearchable: boolean) => {
    const defaultFilter = createFilter({
      ignoreAccents: false,
      stringify: option => option.label,
    });

    if (!isSearchable || minLength === 0) {
      return defaultFilter;
    }

    return (candidate, input) => {
      if (!input || input.length < minLength) {
        return false;
      }

      return defaultFilter(candidate, input);
    };
  };

  private createNoOptionsMessage = (
    minLength: number,
    isSearchable: boolean,
    searchMessage: string,
    defaultMessage: string,
  ) => {
    if (!isSearchable || minLength === 0) {
      return () => defaultMessage;
    }

    return input => (!input.inputValue || input.inputValue.length < minLength ? searchMessage : defaultMessage);
  };

  private orderOptions = (unorderedOptions: Array<ISelectMultiOption<T>>) => {
    const options = unorderedOptions
      ? unorderedOptions.filter(option => option.fixed).concat(unorderedOptions.filter(option => !option.fixed))
      : [];

    return this.props.isSorted ? options.sort(sortOptions) : options;
  };
}
